/**
 * Organization Scope Utilities for Supabase
 * 
 * This file provides standardized utilities for maintaining organization isolation
 * in Supabase queries, ensuring consistent application of multi-tenant boundaries.
 * 
 * ⚠️ IMPORTANT: Always use these utilities for data operations to maintain proper
 * multi-tenant isolation between organizations.
 *
 * Requirements summary:
 * - The Athletes page is the gym relationship list, so staff lookups must include
 *   active and inactive athlete_organizations rows while still staying scoped to
 *   the current organization.
 * - Athlete-access authorization (`canUserAccessAthlete`) now honors an ACTIVE
 *   `organization_staff` row in the org, not just the stored `auth_users.user_type`
 *   (plan 2026-06-02-001 / issue #637, U6/KTD9). A parent who has been granted staff
 *   access keeps `user_type='parent'` but still needs to view/edit other families'
 *   athletes. The own-child path stays the FIRST check and short-circuits as before,
 *   so a normal parent (no staff row) is completely unaffected and pays zero extra
 *   queries. The staff-row fallback only fires when the cheap own-child check denies.
 * - A demo viewer on the shared demo gym has a `demo_org_viewers` grant instead of a
 *   staff row (#1354) and is read-only staff-equivalent there; see
 *   `hasStaffLevelAthleteAccess`.
 */

import { supabase } from './client';

import { createLogger } from '@/utils/logger/devLogger';
// Issue #978: the per-org "may edit athlete profiles" capability check (client mirror of
// RLS helper fga_staff_can_manage_athlete) lives in its own module so the rule is defined
// once and importable from both here (edit intent) and any other edit path (DRY).
import { hasManageAthletesCapability } from './athleteEditCapability';
// Org-membership predicate lives in its own leaf module so this file and
// ./athleteCrossover can both use it without an import cycle. Imported here for
// canUserAccessAthlete and re-exported below so existing import paths still work.
import { isAthleteInOrganization } from './athleteOrganizationMembership';
// #1354: a demo viewer browsing the shared demo gym holds a demo_org_viewers grant
// instead of an organization_staff row. Same helper the server gates
// (organizationScopeMiddleware) and the client role hook (useTeamPermissions) use, so
// "who counts as staff here?" is answered the same way on every surface.
import { hasDemoViewerGrant } from '@/utils/demo/demoMembership';

const logger = createLogger('organizationScope');

/**
 * Adds organization_id filter to a Supabase query for standard tables.
 *
 * This is a generic wrapper used across many tables, so it accepts and returns
 * an untyped query. The Supabase PostgrestFilterBuilder generic has become
 * increasingly narrow (now requires 4-7 type arguments tied to a specific
 * table's Row/Relationships), which is incompatible with a cross-table helper.
 * Using a loose type here keeps the wrapper usable everywhere without forcing
 * every caller to re-specify a fully parameterized builder type.
 */
export function withStandardOrganizationScope<Q>(
  query: Q,
  organizationId: string | null | undefined
): Q {
  if (!organizationId) {
    logger.warn('No organization ID provided for organization scoping');
    return query;
  }

  return (query as any).eq('organization_id', organizationId) as Q;
}

/**
 * Handles the special case for athletes table using the join table
 * This is an async function that needs to be awaited
 * 
 * @param organizationId The organization ID to filter by
 * @returns Promise that resolves to an array of athlete IDs in the organization
 */
export async function getAthleteIdsInOrganization(
  organizationId: string | null | undefined
): Promise<string[]> {
  if (!organizationId) {
    logger.warn('No organization ID provided for athlete organization scoping');
    return [];
  }
  
  // Get every athlete ID that belongs to this organization. Visibility filters
  // decide whether inactive/past athletes appear in the default roster.
  const { data, error } = await supabase
    .from('athlete_organizations')
    .select('athlete_id')
    .eq('organization_id', organizationId);
    
  if (error) {
    logger.error('Error fetching athlete IDs for organization:', error);
    return [];
  }
  
  return (data || []).map(item => item.athlete_id);
}

/**
 * Unified organization scope function that automatically detects table type
 * 
 * @param query The Supabase query to modify
 * @param organizationId The organization ID to filter by
 * @param options Optional configuration with table name
 * @returns The query with appropriate organization scoping
 */
export function withOrganizationScope<Q>(
  query: Q,
  organizationId: string | null | undefined,
  options?: { table?: string }
): Q {
  if (!organizationId) {
    logger.warn('No organization ID provided for organization scoping');
    return query;
  }
  
  // Get the table name from options or try to extract it
  const tableName = options?.table || extractTableName(query);
  
  // Special case for athletes table
  if (tableName === 'athletes') {
    // For the athletes table, we should verify membership through the join table
    // but we'll handle this differently since we can't use the in-filter with a subquery
    // (that was causing the UUID format error)
    return query; // Return as-is, expecting verification to happen elsewhere
  }
  
  // Special case for team_athletes table - organization scoping comes through teams
  if (tableName === 'team_athletes') {
    // For team_athletes, we don't apply organization_id directly since it doesn't have that column
    // The organization scoping must be handled through the teams relationship
    return query; // Return as-is, relying on teams relationship for scoping
  }
  
  // For all other tables, apply standard organization_id filter
  return withStandardOrganizationScope(query, organizationId);
}

/**
 * Attempts to extract table name from a Postgrest query
 * 
 * @param query The query to extract from
 * @returns The table name or 'unknown'
 */
function extractTableName(query: any): string {
  try {
    // Option 1: Look for the table name in the query object
    if (query.from) return query.from;
    
    // Option 2: Try to get it from the builder if available
    if (query.builder && query.builder.from) return query.builder.from;
    
    // Option 3: Get it from the function that created the query
    const queryStr = query.toString();
    if (queryStr) {
      const match = queryStr.match(/from\s+([a-zA-Z0-9_]+)/i);
      if (match && match[1]) return match[1];
    }
  } catch (err) {
    logger.warn('Could not extract table name from query:', err);
  }
  
  return 'unknown';
}

// `isAthleteInOrganization` now lives in ./athleteOrganizationMembership (a leaf
// module shared with ./athleteCrossover to avoid an import cycle). Imported above
// for canUserAccessAthlete; re-exported here so existing
// `@/utils/supabase/organizationScope` import paths are unchanged.
export { isAthleteInOrganization };

// ==============================================================================
// CROSSOVER ATHLETE DETECTION FUNCTIONS
// ==============================================================================
// Extracted to ./athleteCrossover.ts to keep this module under the utils/** 400-line
// `max-lines` cap. Re-exported here so existing `@/utils/supabase/organizationScope`
// import paths keep working unchanged (pure refactor — no behavior change).
export {
  isAthleteCrossoverInOrganization,
  getCrossoverAthletesInOrganization,
  getAthleteCrossoverDetails,
  getBatchAthleteCrossoverDetails,
} from './athleteCrossover';
export type { CrossoverDetails } from './athleteCrossover';

/**
 * Returns true when the user has an ACTIVE `organization_staff` row in the org.
 *
 * This is the single source of truth for "is this account currently staff here?" —
 * the same status-filtered lookup `requireStaffRole` runs at the request layer
 * (`organizationScopeMiddleware.ts`). It deliberately keys on the row, NOT on
 * `auth_users.user_type`, so a parent-typed account that was granted staff access
 * (plan 2026-06-02-001 / issue #637) is recognized, while an inactive (deactivated)
 * staff row is not. Reused by `canUserAccessAthlete` and `athleteClientService` so the
 * staff-row check is defined once (DRY).
 *
 * @param userId The auth user id to check.
 * @param organizationId The organization context (required; staff is always org-scoped).
 * @returns Promise resolving to true only if an active staff row exists.
 */
export async function hasActiveStaffRow(
  userId: string,
  organizationId: string | null | undefined
): Promise<boolean> {
  if (!organizationId) {
    return false;
  }

  const { data: staffRecord, error: staffError } = await supabase
    .from('organization_staff')
    .select('id')
    .eq('auth_user_id', userId)
    .eq('organization_id', organizationId)
    .eq('status', 'active')
    .maybeSingle();

  if (staffError) {
    logger.warn('Error checking active organization_staff row:', staffError);
    return false;
  }

  return !!staffRecord;
}

/**
 * Does this user get staff-level reach over the org's athletes, for this intent?
 *
 * Two ways to qualify, and the difference matters:
 *  - an ACTIVE `organization_staff` row — the normal case. EDIT additionally needs
 *    the per-org `can_manage_athletes` capability (#978), so a coach keeps gym-wide
 *    read but cannot edit athlete PII.
 *  - a demo-viewer grant on the shared demo gym (#1354) — READ ONLY, always. Before
 *    #1354 a viewer held a real "Demo Viewer" owner row and passed the staff check
 *    above; the grant replaced that row without this client gate learning about it,
 *    which is why every athlete profile in the demo gym read "Athlete not found for
 *    this organization." Edit intent is refused outright rather than deferred to a
 *    capability lookup: the demo is read-only, and `rejectDemoWrite` / `useDemoGuard`
 *    already block the writes themselves.
 *
 * Membership in the org is NOT decided here — both callers still confirm the athlete
 * actually belongs to `organizationId`, so this can only ever widen access within one
 * gym, never across gyms.
 */
async function hasStaffLevelAthleteAccess(
  userId: string,
  organizationId: string,
  intent: 'view' | 'edit'
): Promise<boolean> {
  if (await hasActiveStaffRow(userId, organizationId)) {
    return intent !== 'edit' || (await hasManageAthletesCapability(userId, organizationId));
  }

  if (intent === 'edit') {
    return false;
  }

  // Cheap for real gyms: the helper returns false without a query for any org that
  // is not the shared demo gym.
  return await hasDemoViewerGrant(organizationId, userId, supabase);
}

/**
 * Checks if the current user can access a specific athlete
 * Handles both staff (organization access) and parent (family relationship) access patterns
 *
 * Dual-path note (plan 2026-06-02-001 / issue #637, U6/KTD9): athlete access is granted
 * by an ACTIVE `organization_staff` row regardless of `auth_users.user_type`. For a
 * `user_type==='parent'` caller the cheap own-child relationship check runs FIRST and
 * short-circuits unchanged; only when it would DENY (the athlete is not the caller's own
 * child) do we fall back to the active-staff-row check and, if present, the existing
 * staff org-membership path. A normal parent (no staff row) is unaffected and incurs no
 * extra query.
 *
 * EDIT vs VIEW intent (issue #978): staff READ access is gym-wide and role-blind
 * (coaches were given gym-wide read/evaluation access in PR #470). Staff WRITE access
 * must additionally require the per-org `can_manage_athletes` capability — a coach is
 * evaluation-only and must NOT edit athlete PII. Pass `intent: 'edit'` from the save
 * path so the staff branches also check `hasManageAthletesCapability`. `intent` defaults
 * to `'view'`, so every existing caller is behavior-neutral and read access stays
 * role-blind. This is the client mirror of the RLS helper `fga_staff_can_manage_athlete`;
 * RLS remains the real boundary.
 *
 * @param athleteId The athlete ID to check access for
 * @param organizationId The organization ID context
 * @param intent 'view' (default, read access) or 'edit' (additionally requires can_manage_athletes for staff)
 * @returns Promise resolving to true if user can access the athlete for the given intent
 */
export async function canUserAccessAthlete(
  athleteId: string,
  organizationId: string | undefined | null,
  intent: 'view' | 'edit' = 'view'
): Promise<boolean> {
  try {
    // Get current session to identify user
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session) {
      logger.warn('No valid authentication session found');
      return false;
    }

    const userId = session.user.id;

    // Get user type from auth_users table (consistent with OrganizationContext/UserRoleContext).
    // Session metadata may be missing user_type for accounts created before it was added,
    // causing parents to be misidentified as gym_staff and denied access.
    const { data: authUser, error: authUserError } = await supabase
      .from('auth_users')
      .select('user_type')
      .eq('id', userId)
      .maybeSingle();

    if (authUserError) {
      logger.warn('Failed to fetch user type from auth_users:', authUserError);
    }

    const userType = authUser?.user_type
      || session.user.user_metadata?.user_type;

    if (!userType) {
      logger.warn('User type not found for user:', userId);
      return false;
    }

    // For Super Admins: full access to everything (organizationId optional)
    if (userType === 'cheervue_admin') {
      return true;
    }

    // For athlete users: Check if they are accessing their own record
    if (userType === 'athlete') {
      // For athlete users: Check if they are accessing their own record
      // This allows independent athletes to access their own profile data
      // even before being assigned to an organization.
      const { data: athleteRecord, error: athleteError } = await supabase
        .from('athletes')
        .select('id')
        .eq('auth_user_id', userId)
        .eq('id', athleteId)
        .maybeSingle();
      
      if (athleteError) {
        logger.warn('Error checking athlete self-access:', athleteError);
        return false;
      }
      
      // If athlete matched their own record — allow access.
      return !!athleteRecord;
    }

    // For staff users: Check organization membership (organizationId required)
    if (userType === 'gym_staff') {
      if (!organizationId) {
        logger.warn('Staff users require organizationId');
        return false;
      }

      // Verify the caller has staff-level reach in this organization first, then
      // confirm the athlete is in that organization. Both gates share
      // hasStaffLevelAthleteAccess so the gym_staff and granted-parent paths stay
      // identical (DRY) — including the #978 edit capability and the #1354 demo
      // viewer's read-only access.
      if (!(await hasStaffLevelAthleteAccess(userId, organizationId, intent))) {
        return false;
      }

      return await isAthleteInOrganization(athleteId, organizationId);
    }

    // For parent users: Check family relationship FIRST (organizationId optional).
    // This own-child check is the cheap, primary path and short-circuits exactly as
    // before — a normal parent never reaches the staff fallback below.
    if (userType === 'parent') {
      // ✅ UPDATED 2025-11-13: parent_athlete_relationships.parent_id now stores auth_users.id directly
      // No need to query parents table - use userId directly
      const { data: relationship, error: relationshipError } = await supabase
        .from('parent_athlete_relationships')
        .select('athlete_id')
        .eq('parent_id', userId)  // ✅ Direct auth_user_id usage (migrated schema)
        .eq('athlete_id', athleteId)
        .maybeSingle();

      if (relationshipError) {
        logger.warn('Error checking parent-athlete relationship:', relationshipError);
        return false;
      }

      // Own-child relationship confirmed — allow access.
      // Parents who register through the public signup flow may not have a
      // parent_organizations record yet, so organization membership must NOT
      // gate access to their own child's profile.
      if (relationship) {
        return true;
      }

      // Not the caller's own child. Before denying, honor an ACTIVE organization_staff
      // row (plan 2026-06-02-001 / issue #637, U6/KTD9): a parent-typed account that was
      // granted staff access keeps user_type='parent' but should reach other families'
      // athletes via the same org-membership path gym_staff uses. This lookup runs ONLY
      // on the own-child miss, so the common case (parent viewing their child) adds no
      // extra query. An inactive (deactivated) staff row does NOT grant access. A
      // parent-typed account exploring the demo gym qualifies the same way a
      // staff-typed one does (#1354) — the grant is not user-type-specific.
      if (organizationId && (await hasStaffLevelAthleteAccess(userId, organizationId, intent))) {
        return await isAthleteInOrganization(athleteId, organizationId);
      }

      // No own-child relationship, no active staff row, no demo-viewer grant — deny.
      return false;
    }

    // NOTE: the own-child branch above returns true regardless of `intent` — a guardian
    // editing their OWN child is governed by the family-permission RLS path
    // (fga_athletes_guardian_profile_update / assertGuardianMayEditAthleteSection), not
    // by `can_manage_athletes`, which is a STAFF capability. The `intent === 'edit'`
    // staff gate only applies on the staff-row fallback above (a granted-staff parent
    // editing OTHER families' athletes), matching the RLS boundary.

    // Unknown user type
    return false;
  } catch (error) {
    logger.warn('Exception in canUserAccessAthlete:', error);
    return false;
  }
}
